/**
 * Game shell: fixed-timestep driver + presentation.
 *
 * The sim is advanced ONLY by stepRun at a fixed timestep. Everything
 * this file adds — shake, hit-stop, slow-mo, particles, audio — is
 * presentation. Slow-mo and hit-stop change how many fixed steps run per
 * frame; they never change what a step does.
 */
import {
  loadConfig, chapterList, initRun, stepRun, recomputeStats, hashState,
  Phase, Outcome, Ev, TICK_RATE, EMPTY_META, EVENT_STRIDE,
  PICKUP_RECRUIT,
  type RunState, type RawConfig, type TickInput,
} from '@bonwarsy/run-engine';
import rawJson from '../../../config/generated/game-config.json';
import { followInput, laneInput, laneOf, difficultyTier, volleySize, unitHp } from '@bonwarsy/run-engine';
import { createScene, SCREEN_X_SIGN, type SceneHandles, type TickSnapshot } from './three/scene3d.js';
import { ENEMY_TINT, ENEMY_DARK } from './textures.js';
import { Juice, juiceConfig } from './juice.js';
import { Audio } from './audio.js';
import { Input } from './input.js';
import { installTuningPanel } from './render/tuning.js';

const STEP_MS = 1000 / TICK_RATE;
/**
 * The level currently loaded. Every difficulty axis -- flow acceleration,
 * enemy and boss toughness, how far up the weapon ladder the level reaches
 * -- comes from that level's row in chapters.csv, so the config is rebuilt
 * whenever the player picks a different isle.
 */
const CHAPTERS = chapterList(rawJson as unknown as RawConfig);
/**
 * ISLES ACTUALLY SHIPPED.
 *
 * Isles 2-11 are WITHHELD, not locked behind skill. Their gate schedule is
 * corrupt (A-39): `gates.csv` carries 496 `subtract` gates, every `multiply`
 * at x0.5, and ZERO `add` gates, so squad size can only ever fall. Measured,
 * the squad sits at 1-2 while 156+ bodies are on the field by t10 and the run
 * dies at 24-56s having seen a third of its waves. No player can clear one.
 *
 * Shipping them as "unlocked by clearing the isle before" would promise
 * content that cannot be completed, and would spend the player's first win on
 * a door into a level that beats them for reasons they cannot read. One
 * finished isle outranks eleven broken ones (M6).
 *
 * Isle 12 (authored 2026-08-22, docs/ISLE-12-SPEC.md, independently
 * calibrated 2026-08-23) is ALSO shipped despite sitting past the withheld
 * block -- shipped isles are no longer a contiguous prefix, so a scalar
 * cutoff can't express membership and this is a set instead. Raise 2-11
 * into it when A-25 re-earns the generator's provenance and the winrate
 * sweep is green — not before, and not by tuning on top of the corrupt table.
 */
const SHIPPED_ISLES = new Set<number>([1, 12]);
// Isle length shown on the map chips: time until that isle's finale wave.
const CHAPTER_SECS = new Map<number, number>();
for (const w of (rawJson as unknown as { waves: { chapter: number; startTick: number }[] }).waves) {
  const t = Math.round(w.startTick / 60);
  if (t > (CHAPTER_SECS.get(w.chapter) ?? 0)) CHAPTER_SECS.set(w.chapter, t);
}
let chapter = 1;
/**
 * DEBUG ONLY: `?chapter=N` boots straight into isle N, bypassing the
 * SHIPPED_ISLES menu gate below. The gate only hides the isle-select
 * chip -- the run engine has always accepted any authored chapter number,
 * so this still reaches the withheld 2-11 batch (corrupt, for inspection
 * only) without touching SHIPPED_ISLES or the menu logic itself. Isle 12
 * no longer needs this path -- it is a normal grid entry now. Out-of-range
 * values are ignored.
 */
const DEBUG_CHAPTER = Number(new URLSearchParams(location.search).get('chapter'));
if (DEBUG_CHAPTER >= 1 && DEBUG_CHAPTER <= CHAPTERS.length) chapter = DEBUG_CHAPTER;
let cfg = loadConfig(rawJson as unknown as RawConfig, chapter);
/**
 * Lane count is a PER-ISLE axis now, so it cannot be captured once at boot.
 * Isle 3 runs three lanes and isle 4 runs two; a value frozen from chapter
 * one would draw the wrong number of lane dividers and aim the bot at
 * lanes that do not exist.
 */
let lanes = Math.max(1, cfg.rules.laneCount);

const host = document.getElementById('stage')!;
const hud = document.getElementById('hud')!;
/**
 * Persistent controls, built ONCE. They cannot live in #hud: that element
 * is rewritten whenever the HUD key changes -- about ten times a second
 * during play -- and a button that is replaced mid-gesture is detached
 * before the tap completes, so it can never be pressed. That is the same
 * defect that once made the level-up cards unresponsive.
 */
const controls = document.getElementById('controls')!;
/**
 * SCREEN-SPACE STAT BANNER (Z-Route Build Plan, Priority 7's flagged
 * remainder). Same "outside #hud" reasoning as `controls` above -- a
 * mid-flight CSS animation would be cut off by #hud's own innerHTML
 * rewrite. `go` is toggled off then back on to force the animation to
 * restart on a repeat trigger (merely re-adding an already-present class
 * is a no-op; a browser only replays a CSS animation when the class
 * transitions from absent to present).
 */
const bannerTxt = document.querySelector('#banner .txt') as HTMLElement;
function showBanner(text: string, colorHex: number): void {
  bannerTxt.classList.remove('go');
  bannerTxt.style.color = `#${colorHex.toString(16).padStart(6, '0')}`;
  bannerTxt.textContent = text;
  // FIT-TO-WIDTH, NOT A FIXED CLAMP (found via a real overflow measurement,
  // not assumed): a pure CSS `clamp(28px, 11vw, 64px)` sized "SWIFT
  // CURRENTS +150%" (the longest upgrade name plus a 3-digit magnitude,
  // measured against `config/generated/game-config.json`'s own upgrade
  // list) at 831px wide against a 390px viewport -- more than double,
  // overflowing both edges. 0.62em/char is a bold-condensed-uppercase
  // estimate, not exact per-glyph metrics, so it undershoots slightly by
  // design (a banner a touch smaller than the frame reads fine; one that
  // clips off both edges does not).
  const fit = Math.min(64, Math.max(22, (window.innerWidth * 0.92) / (text.length * 0.62)));
  bannerTxt.style.fontSize = `${fit.toFixed(1)}px`;
  // Reflow, so the class removal above actually lands before it's re-added.
  void bannerTxt.offsetWidth;
  bannerTxt.classList.add('go');
}
controls.innerHTML = '<button id="pause" aria-label="Pause">II</button>'
  + '<button id="mute" aria-label="Mute">\u{1F50A}</button>';
function paintControls(): void {
  const m = document.getElementById('mute');
  if (m) m.textContent = muted ? '\u{1F507}' : '\u{1F50A}';
  const p = document.getElementById('pause');
  if (p) p.style.visibility = started && !awaitingResume ? 'visible' : 'hidden';
}

/**
 * A-45 REMOVED (founder finding, 2026-09-02): the level-up choice cards
 * ("3 choices popping up... during play") were reported as constant,
 * unexplained on-screen clutter -- removed outright, same call as the
 * dash button. Every level-up now falls straight through to the
 * unattended path the sim already has (`s.boonWindowTicks` simply never
 * gets a pick command, so it always resolves via the seeded random draw)
 * -- upgrades still apply every level, just without a UI popup asking
 * the player to choose one.
 */

let scene3d: SceneHandles | null = null;
const juice = new Juice(juiceConfig);
const audio = new Audio(juiceConfig);
let input: Input;

let state: RunState;
let acc = 0;
let last = 0;
/**
 * Fixed-timestep interpolation (fix C, 2026-09-05): the render layer's
 * own copy of the fields it interpolates, captured immediately before
 * each `stepRun()` -- NOT in RunState (Iron Rule 1 stands; a run still
 * replays from config+seed+inputLog alone, this is presentation
 * smoothing over ticks that already happened). ONE object, mutated in
 * place every tick and at every accumulator reset -- never reallocated,
 * so there is no per-frame or per-tick allocation from this.
 */
const prevSnap: TickSnapshot = { worldY: 0, playerX: 0, playerY: 0 };
/** Last computed interpolation alpha -- exposed on `__bonwarsy` below,
 * purely for the fix-C accept check (alpha stays within [0,1] on every
 * frame of a capture). Not read by anything gameplay-relevant. */
let lastAlpha = 0;
/** Sync `prevSnap` to the CURRENT state. Called before every `stepRun()`
 * (so it holds the tick immediately prior to the one about to run) and
 * at every accumulator reset (`acc = 0`, run start/resume/isle change)
 * so `prev === curr` exactly then -- alpha has no effect on the first
 * frame after a reset, no NaN, no jump. */
function syncPrevSnap(): void {
  prevSnap.worldY = state.worldY;
  prevSnap.playerX = state.playerX;
  prevSnap.playerY = state.playerY;
}
/**
 * `?seed=N` (dev-only, capture harness): overrides the default seed at
 * boot. Combined with `?bot=1`, `frame(sim tick)` of two separate runs
 * with the same seed produces identical state -- the sim has zero
 * Math.random()/Date.now() (Iron Rule 1) and the bot's followInput is
 * itself a pure function of state, so this is deterministic by
 * construction, not by anything this flag adds. Ships inert.
 */
const DEBUG_SEED = Number(new URLSearchParams(location.search).get('seed'));
let seed = Number.isFinite(DEBUG_SEED) && DEBUG_SEED > 0 ? DEBUG_SEED : 1;

/**
 * AUTOPILOT (dev-only). `?bot=1` drives the run with the reference
 * player's lane scoring through the game's own input pipeline — the
 * capture harness's evidence frames must show a run that is actually
 * being PLAYED, and keyboard actuation cannot pass the squad-1 opening
 * that a 60Hz follow-input player clears 63% of the time. Ships inert:
 * without the query param this is a dead branch.
 *
 * MIRROR — source of truth is scripts/reference-policy.ts (decide()).
 * Compressed: same threat horizon, same bodies-unit scoring, same
 * armoured-gate set, same dwell. Only the monolith stanza is dropped
 * (position-neutral for framing).
 */
const BOT_MODE = new URLSearchParams(location.search).get('bot') === '1';
/**
 * `?tune=1` (dev-only): loads the live Tweakpane panel for the
 * run-scene motion stages' tunable constants (render/tuning.ts).
 * Dynamically imported so `tweakpane` never enters the shipping bundle
 * for a normal player. Ships inert.
 */
if (new URLSearchParams(location.search).get('tune') === '1') void installTuningPanel();
/**
 * DEBUG ONLY (founder, 2026-08-20): 12 GUN_LOOKS profiles are fully
 * authored (scene3d.ts) but weapon_cap per chapter and armscrate draw
 * luck mean tier 3+ has never actually been exercised in a real session
 * -- not a rendering defect, a progression-pacing one. `?weaponTier=N`
 * (1-12) forces s.weaponIdx once at run start so every tier can be
 * looked at directly. Never read by sim/balance code paths.
 */
const DEBUG_WEAPON_TIER = Number(new URLSearchParams(location.search).get('weaponTier')) || 0;
/**
 * ISLE 1 FAIL-CONDITION VARIANTS (founder, 2026-08-21). `?variant=A` is
 * MEANINGFUL FAILURE (the existing default: a squad wipe ends the run
 * mid-level). `?variant=B` is THRESHOLD AT THE BOSS: the sim floors
 * squadSize at 1 and resolves win/lose the instant the boss spawns,
 * comparing squadSize against RunState.clashThreshold instead of fighting
 * its hp -- see RunState.variant in @bonwarsy/run-engine. No param = A.
 */
const DEBUG_VARIANT = new URLSearchParams(location.search).get('variant') === 'B' ? 1 : 0;
let botLane = 0;
let botDwell = 0;
function botTargetX(): number {
  const r = cfg.rules;
  const L = Math.max(1, r.laneCount);
  // FORGE ISLES: the autopilot plays the real loop — charge the blocks
  // while the horde lane's imminent arrivals are clearable, hold the
  // horde otherwise; scripted opening on the reward lane. Mirrors
  // scripts/reference-policy.ts (instrument v3).
  if (r.rewardLane >= 0 && r.rewardLane < L) {
    const hordeLane = r.rewardLane === 0 ? 1 : 0;
    let hordeT = 0;
    for (let i = 0; i < state.ep.cap; i++) {
      if (!state.ep.alive[i] || state.eIsBoss[i] === 1) continue;
      const dy = state.eY[i]! - state.playerY;
      if (dy < 0 || dy > 26) continue;
      if (laneOf(r.arenaWidth, L, state.eX[i]!) === hordeLane) {
        hordeT += 1 - (dy / 26) * 0.75;
      }
    }
    const w = cfg.weapons[state.weaponIdx]!;
    const kr = Math.max(0.5,
      Math.min(r.maxProjectilesPerVolley, state.squadSize) * (60 / Math.max(1, w.cooldownTicks)) * 0.22);
    let boss = -1;
    for (let i = 0; i < state.ep.cap; i++) {
      if (state.ep.alive[i] && state.eIsBoss[i] === 1) { boss = state.eX[i]!; break; }
    }
    if (boss >= 0) return boss;

    // GATE AWARENESS (root-cause fix, 2026-08-20): this branch never read
    // state.gp at all, so a scripted gate landing in whichever lane the
    // charge/hold heuristic below already wanted was invisible to the
    // decision -- chapter 1's c1_g0_r (an unguarded subtract sitting in
    // rewardLane) was walked into blind every seed, always inside the
    // scripted-opening window (tick < 14*60), because nothing here ever
    // looked at it. Loop copied verbatim from the non-forge branch below
    // (same gdy<20 imminence window, same op-scoring formula) rather than
    // special-cased for this one gate id, so it generalises to any gate
    // any forge-isle chapter ships.
    const value = new Array<number>(L).fill(0);
    let gdy = 1e9;
    for (let i = 0; i < state.gp.cap; i++) {
      if (!state.gp.alive[i]) continue;
      const dy = state.gY[i]! - state.playerY;
      if (dy > 0 && dy < gdy) gdy = dy;
    }
    if (gdy < 20) {
      for (let i = 0; i < state.gp.cap; i++) {
        if (!state.gp.alive[i]) continue;
        if (Math.abs((state.gY[i]! - state.playerY) - gdy) > 0.5) continue;
        const op = state.gOp[i]!, v = state.gValue[i]!;
        const sc = op === 0 ? v : op === 1 ? -v : op === 2 ? (v - 1) * state.squadSize
          : op === 3 ? 18 : op === 4 ? v : op === 5 ? 18 : op === 6 ? 24
          : op === 7 ? 26 : v;
        value[state.gLane[i]!]! += sc;
      }
    }

    const charge = state.tick < 14 * 60 || hordeT <= kr * 3.0;
    const candidateLane = charge ? r.rewardLane : hordeLane;
    const otherLane = candidateLane === r.rewardLane ? hordeLane : r.rewardLane;
    // Same signal, same units (bodies) the non-forge net formula already
    // uses -- no new weight invented. Gate value can flip the charge/hold
    // default in either direction, including inside the scripted opening,
    // because it is compared unconditionally, not gated behind `!charge`.
    const lane = value[otherLane]! > value[candidateLane]! ? otherLane : candidateLane;
    return ((lane + 0.5) / L) * r.arenaWidth;
  }
  let boss = -1;
  const threat = new Array<number>(L).fill(0);
  const uHp = r.squadUnitHp;
  for (let i = 0; i < state.ep.cap; i++) {
    if (!state.ep.alive[i]) continue;
    if (state.eIsBoss[i] === 1) { boss = state.eX[i]!; continue; }
    const dy = state.eY[i]! - state.playerY;
    if (dy < 0 || dy > 26) continue;
    const def = cfg.enemies[state.eDefIdx[i]!]!;
    const imminence = def.attackType === 'ranged' ? 1 : 1 - (dy / 26) * 0.75;
    threat[laneOf(r.arenaWidth, L, state.eX[i]!)]! += (def.contactDamage / uHp) * imminence;
  }
  if (boss >= 0) { botDwell = 0; return boss; }
  const value = new Array<number>(L).fill(0);
  let gdy = 1e9;
  for (let i = 0; i < state.gp.cap; i++) {
    if (!state.gp.alive[i]) continue;
    const dy = state.gY[i]! - state.playerY;
    if (dy > 0 && dy < gdy) gdy = dy;
  }
  if (gdy < 20) {
    for (let i = 0; i < state.gp.cap; i++) {
      if (!state.gp.alive[i]) continue;
      if (Math.abs((state.gY[i]! - state.playerY) - gdy) > 0.5) continue;
      const op = state.gOp[i]!, v = state.gValue[i]!;
      const sc = op === 0 ? v : op === 1 ? -v : op === 2 ? (v - 1) * state.squadSize
        : op === 3 ? 18 : op === 4 ? v : op === 5 ? 18 : op === 6 ? 24
        : op === 7 ? 26 : v;
      value[state.gLane[i]!]! += sc;
    }
  }
  if (botDwell > 0) botDwell--;
  else {
    let bestLane = botLane, bestNet = -1e9;
    for (let l = 0; l < L; l++) {
      let elsewhere = 0;
      for (let o = 0; o < L; o++) if (o !== l) elsewhere += threat[o]!;
      const net = value[l]! + threat[l]! * 4 - elsewhere * 4;
      if (net > bestNet) { bestNet = net; bestLane = l; }
    }
    if (bestLane !== botLane) botDwell = 24;
    botLane = bestLane;
  }
  return ((botLane + 0.5) / L) * cfg.rules.arenaWidth;
}
let bossAnnounced = false;
let dmgTick = 0;
let lean = 0;
let lastPX = 0;
// IMPACT-FX COST CAP (2026-08-21): rings/streaks per rendered frame, reset
// in consumeEvents. A dense wave-clear can push dozens of Hit events
// through one frame; without a cap, cost scales with alive-count with no
// ceiling. See consumeEvents' Ev.Hit case for the gates themselves.
let ringsThisFrame = 0;
let streaksThisFrame = 0;
const MAX_IMPACT_FX_PER_FRAME = 3;
/**
 * Genuinely-splashy weapons only (2026-08-21) -- a numeric aoeRadius
 * threshold can't separate these from melee (hammer's 3.6 is bigger than
 * flak's 1.6) or from direct-fire energy weapons whose aoeRadius crosses
 * a low bar without the weapon reading as an explosive at all.
 */
const IMPACT_RING_PROFILES = new Set(['mortar', 'flak', 'missile']);

/**
 * Local progression. Without a best score a second run is identical to
 * the first, and "retry" has nothing to beat — the cheapest retention
 * lever in the genre and we had none of it.
 */
interface Progress {
  best: number; runs: number; bestSquad: number; bossKills: number;
  muted: boolean; taught: number;
  /** Highest level the player may enter. Beating n unlocks n+1. */
  unlocked: number;
  /** Levels already cleared, for the map's tick marks. */
  cleared: number[];
  /** Selected roster character (index into config.characters). */
  character: number;
  /** Best star rating (0-3) ever earned per chapter, keyed by chapter
   * number. A chapter with no entry has never been taken -- see the
   * level-select grid, which reads this to paint each card's stars. */
  stars: Record<number, number>;
}
const PROGRESS_KEY = 'bonwarsy.progress.v1';
function loadProgress(): Progress {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (raw) {
      const p = JSON.parse(raw) as Partial<Progress>;
      return {
        best: p.best ?? 0, runs: p.runs ?? 0,
        bestSquad: p.bestSquad ?? 0, bossKills: p.bossKills ?? 0,
        muted: p.muted ?? false, taught: p.taught ?? 0,
        unlocked: p.unlocked ?? 1, cleared: p.cleared ?? [],
        character: p.character ?? 0, stars: p.stars ?? {},
      };
    }
  } catch { /* private mode or disabled storage — play without it */ }
  return { best: 0, runs: 0, bestSquad: 0, bossKills: 0, muted: false, taught: 0,
    unlocked: 1, cleared: [], character: 0, stars: {} };
}
function saveProgress(p: Progress): void {
  try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(p)); } catch { /* non-fatal */ }
}
let progress = loadProgress();
let muted = progress.muted;
let beatBest = false;
let recorded = false;
/** rAF `now` the run first observed Ended+BossKilled, or -1 for "not yet
 * (or this run didn't end that way)". Drives the ISLE COMPLETE beat: the
 * live scene holds with a light overlay for ISLE_COMPLETE_BEAT_MS before
 * the normal end-of-run screen (stats, NEXT ISLE) takes over. Death and
 * timeout never set this -- they go straight to the existing screen, as
 * before. */
let bossKilledAt = -1;
const ISLE_COMPLETE_BEAT_MS = 2200;
/**
 * Squad size the instant the boss spawned this run, -1 until then. Variant
 * 0 (every real player) never sets RunState's own clashCrowd/stars --
 * those exist only for variant 1, the offline "threshold at the boss"
 * balance sim (step.ts s.variant===1), which ends the run at spawn instead
 * of fighting it. The corridor's own reach/threshold config (`cfg.rules.
 * clashThresholdT`/`star2Threshold`/`star3Threshold`) is authored either
 * way, so a real run's clash reading is captured here, client-side, at the
 * same event (`Ev.Spawned` with `isBoss`) verify-level.ts's own D check
 * uses to measure crowd-at-boss.
 */
let clashCrowdAtSpawn = -1;

/** First-run teaching: shown until the player actually drags. */
let hasTouched = false;
let started = false;

// INTEGRATION FIX (founder verdict on 44e71d97): the staged-teaching
// banner ("SHOOT THE BARRIER DOWN" etc.) and the two direct-assignment
// banners ("SHOOT THE ROCK — OR HOLD THE LANE", "KILL IT BEFORE IT
// REACHES YOU") are DELETED, not time-limited. TIP_SECONDS (previous
// commit) stopped the banner from drawing past 8s of run time, but the
// entombed-titan event still re-triggers `tipText` every time it fires,
// and multiple titans can be entombed inside the same 8s window — so
// the fix produced a banner that FLICKERS in and out repeatedly instead
// of one that persists, which is worse. Teaching the player what a
// barrier/gate/armory/titan does is a real design need, but it belongs
// to a later, deliberate design pass (a proper tutorial beat, not a
// flashing text overlay retrofitted onto a combat HUD), not a patch on
// top of a patch in this session. `progress.taught` stays in the
// persisted Progress type (harmless, unused) rather than migrating the
// save schema over an HTML banner.
let awaitingResume = false;
let isLandscape = false;

function newRun(nextSeed: number, toChapter = chapter): void {
  if (toChapter !== chapter) {
    chapter = toChapter;
    cfg = loadConfig(rawJson as unknown as RawConfig, chapter);
    lanes = Math.max(1, cfg.rules.laneCount);
    scene3d?.setLanes(lanes);
  }
  seed = nextSeed;
  state = initRun({ config: cfg, meta: EMPTY_META, seed, chapter,
    characterIdx: progress.character, variant: DEBUG_VARIANT });
  if (DEBUG_WEAPON_TIER > 0) {
    state.weaponIdx = Math.max(0, Math.min(cfg.weapons.length - 1, DEBUG_WEAPON_TIER - 1));
  }
  // D9: the sim side has always been fresh here (initRun() builds a new
  // RunState every call) — the render side was not. Every restart path
  // (death/retry, quit-to-menu, isle re-select, character re-pick) funnels
  // through this one function already; this is the one call site that
  // needed to exist, not seven.
  scene3d?.resetForNewRun();
  scene3d?.setTeamColor(cfg.characters[state.characterIdx]?.tint ?? 0xff7a6e);
  recorded = false;
  beatBest = false;
  bossKilledAt = -1;
  clashCrowdAtSpawn = -1;
  audio.reset();
  recomputeStats(state);
  acc = 0; syncPrevSnap();
  bossAnnounced = false;
}

/** Translate this tick's sim events into presentation. Read-only. */
function consumeEvents(s: RunState): void {
  const e = s.events;
  ringsThisFrame = 0;
  streaksThisFrame = 0;
  // D6 (genre-teardown-corridor.md): "individual deaths are cheap; mass
  // deaths are spectacular." Counted across this whole batch so ONE
  // shared moment can fire at the end instead of scaling every
  // individual death's fx (which D9 warns would white out the screen at
  // real wave-clear counts).
  let massDeathCount = 0, massDeathSumX = 0, massDeathSumY = 0;
  /**
   * The damage of the last blow seen this pass, by enemy slot.
   *
   * The sim pushes Damaged for a killing blow and then Killed for the same
   * slot, so by the time Killed arrives the only record of how hard the
   * body was hit is the event two entries back. Death needs it: a body
   * that was erased and a body that simply ran out of health should not
   * fall the same way.
   */
  const lastBlow = new Map<number, number>();
  for (let i = 0; i < e.length; i += EVENT_STRIDE) {
    const kind = e[i]!;
    const subj = e[i + 1]!;
    const p0 = e[i + 2]!;
    const p1 = e[i + 3]!;
    // p2 is `tier` on Hit. It is deliberately not read yet: F1 picks its
    // audio layer and F3 sizes its freeze from it in Step 3, and wiring it
    // in early would change hit-stop feel with no artifact to grade the
    // change against. The sim already carries it, so Step 3 is a read.
    const p3 = e[i + 5]!;
    const p4 = e[i + 6]!;
    /*
     * `a`/`b` are the legacy two-slot reading, kept because every case body
     * below was written against them and rewriting working presentation code
     * is not what this migration is for.
     *
     * The mapping is exact, not approximate: an event WITH a subject used to
     * put that subject in `a` and its first payload in `b`, which is now
     * `subj` and `p0`. An event WITHOUT one used `a`/`b` for its first two
     * payloads, and those are now `p0`/`p1`. `subject === -1` distinguishes
     * the two cases, so this reproduces the old semantics for every kind.
     * New events read the named slots directly instead.
     */
    const a = subj !== -1 ? subj : p0;
    const b = subj !== -1 ? p0 : p1;
    switch (kind) {
      case Ev.Hit: {
        const kindIdx = Math.min(2, s.eDefIdx[subj]);
        const def = s.config.enemies[s.eDefIdx[subj]]!;
        scene3d!.enemyFlash[a] = juiceConfig.flash.enemyHitFrames;
        // Impact scales with the WEAPON, so a mortar landing reads nothing
        // like a rifle round: same particle system, class-scaled count.
        const boom = scene3d!.gunBoom();
        scene3d!.fx.hit(s.eX[a], s.eY[a],
          Math.round(juiceConfig.particles.impactCount * boom), 0xffe08a);
        if (boom >= 1.8) scene3d!.fx.ring(s.eX[a], s.eY[a], 0xffb06a, boom * 0.9, 14);
        // IMPACT TREATMENT, COST-DISCIPLINED (2026-08-20, capped 2026-08-21):
        // aoeRadius and knockback are authored per weapon and consumed by
        // the sim but had zero visual representation. The first pass fired
        // on every Hit for 9 of 12 weapons (aoeRadius>=0.9) -- at ch9's
        // density that's dozens of extra fx calls in a single frame with no
        // ceiling. Three changes, all cost, none removing the feature:
        // KILL-ONLY (a hit that doesn't finish the body is not the moment
        // that reads as "mass destruction" -- the death is), a per-frame
        // cap shared with the knockback streak (MAX_IMPACT_FX_PER_FRAME,
        // above), and a curated weapon list instead of a numeric threshold
        // -- aoeRadius alone can't separate "genuinely splashy" from
        // "melee, which already got its own re-theme" (hammer's 3.6 is
        // BIGGER than flak's 1.6, so no >= cutoff draws that line).
        const lethalHit = p3 === 1;
        if (lethalHit) {
          const hitWeapon = s.config.weapons[s.weaponIdx]!;
          if (IMPACT_RING_PROFILES.has(hitWeapon.vfxProfile) && ringsThisFrame < MAX_IMPACT_FX_PER_FRAME) {
            // Blast ring sized to the weapon's ACTUAL splash radius --
            // mortar's 3.4u reads nothing like flak's 1.6u would.
            scene3d!.fx.ring(s.eX[a], s.eY[a], 0xffb06a, hitWeapon.aoeRadius, 16);
            ringsThisFrame++;
          }
          if (hitWeapon.knockback >= 0.9 && streaksThisFrame < MAX_IMPACT_FX_PER_FRAME) {
            // A shove streak along the hit direction, length scaled by the
            // weapon's own knockback value -- storm_hammer (2.40) and
            // arc_blade (1.60) throw a real line, missile/mortar a shorter
            // one. Kept as a magnitude threshold, not the ring's curated
            // list: knockback is a distinct axis from splash (melee SHOULD
            // shove hard), so the existing 4-of-12 set already reads right.
            const kdx = s.eX[a] - s.playerX, kdy = s.eY[a] - s.playerY;
            const kAngle = Math.atan2(kdx, kdy);
            scene3d!.fx.tracer(s.eX[a], s.eY[a], kAngle,
              hitWeapon.knockback * 0.6, 0xffffff);
            streaksThisFrame++;
          }
        }
        // Damage is p4 now. `tier` (p2) and `lethal` (p3) are handed over by
        // the sim and must not be re-derived from it.
        const dmg = p4;
        const heavy = dmg >= juiceConfig.hitStop.heavyDamageThreshold;
        // Only a sample of hits gets a number. At this fire rate every
        // impact spawning text buries the horde under its own damage log.
        if (heavy || (dmgTick++ & 3) === 0) {
          // The sample rate controls VOLUME, not CO-LOCATION: at point-
          // blank horde contact, several enemies standing shoulder to
          // shoulder each land at nearly the same world position, so their
          // labels rendered exactly on top of each other -- a stack of
          // identical numbers, unreadable, at the single moment readability
          // matters most. Jittered per enemy SLOT (not per frame), so one
          // body's own numbers stay in a consistent little cloud instead of
          // hopping around, while its neighbours land somewhere else.
          const jx = ((a * 53) % 11 - 5) * 0.055;
          const jy = ((a * 29) % 7 - 3) * 0.04;
          scene3d!.fx.label(s.eX[a] + jx, s.eY[a] + 0.35 + jy, String(Math.round(dmg)),
            heavy ? 0xffd24a : 0xffe9b0, heavy ? 17 : 12);
        }
        // PUNCTUATE A BIG HIT, DON'T FIRE ON EVERY CONTACT (founder,
        // 2026-08-19, sim-wallclock-ratio). Two things had to change,
        // found in that order. First: `addHitStop` used to fire on EVERY
        // `Ev.Hit` unconditionally -- the ordinary (non-lethal, non-boss)
        // case is now gated behind the SAME `heavy` (dmg >=
        // heavyDamageThreshold) check this file already computes for
        // screenshake two lines below, so a chip-damage tap against a
        // grunt no longer freezes anything. That alone was NOT enough --
        // measured live after it, wave 10 still showed `hitStopMs>0` on
        // ~22% of frames (see `juice.ts`'s `HIT_STOP_COOLDOWN_MS` comment
        // for the full before/after numbers), because KILLS during a
        // dense wave-clear burst land far more often than `lethalMs`
        // (80ms) takes to decay, and `Math.max`-based re-triggering never
        // let the timer reach 0. The actual fix is `Juice`'s own cooldown
        // gate (`juice.ts`) -- a hit-stop now refuses to retrigger while
        // one is active or in its post-freeze cooldown, REGARDLESS of
        // category, which is what makes it punctuation instead of a
        // mode. Boss hits pass `force=true` to bypass that gate: with
        // exactly one boss ever on screen, its hits can't run away the
        // way kills against a whole horde can, and a boss beat should
        // never be swallowed by an ordinary kill's cooldown.
        const lethal = p3 === 1;
        const hs = juiceConfig.hitStop;
        if (def.isBoss) {
          juice.addHitStop(hs.titanMs, true);
        } else if (lethal) {
          juice.addHitStop(hs.lethalMs);
        } else if (heavy) {
          juice.addHitStop(hs.normalMs);
        }
        if (heavy) {
          juice.addShake(juiceConfig.screenshake.kill * boom * 0.8);
        }
        void kindIdx;
        lastBlow.set(a, dmg);
        // THE BODY REACTS TO BEING SHOT.
        //
        // Severity is measured against what this blow took from THIS body
        // -- damage over damage-plus-what-is-left -- so a grunt and a
        // brute both react to "half of what I had" the same way, and the
        // renderer needs no balance number to decide. The sim has already
        // subtracted the damage by the time the event is read, so eHp is
        // the health remaining.
        //
        // Nothing is registered for a lethal blow: that body is a corpse
        // this frame and the corpse pool owns its performance.
        const left = s.eHp[a] ?? 0;
        if (left > 0) {
          scene3d!.enemyHit(a, dmg / (dmg + left), s.playerX, s.playerY,
            s.eX[a], s.eY[a]);
        }
        audio.hit(heavy);
        break;
      }
      case Ev.Death: {
        // defIdx is carried on the record (p3) because the subject's pool
        // slot may already be recycled by the time this is read.
        const defIdx = p3;
        const def = s.config.enemies[defIdx]!;
        const kindIdx = Math.min(2, defIdx);
        // ddx/ddy is the killing blow's own heading (p1, and p2 which is
        // deliberately not given a named slot above -- see that comment --
        // read raw at e[i+4]). A splash kill carries no blow (ddx=ddy=0);
        // every direct hit does, and it is the SAME vector Ev.Hit carried
        // for this exact shot the moment earlier in this same tick -- nothing
        // here is inferred, it rides the record.
        const ddx = p1, ddy = e[i + 4]!;
        // `overkill` is the killing blow measured against the body's base
        // health: 1 is a round that just finished it, 3 is a body erased.
        // Base health rather than health-at-death because the sim clamps
        // the overshoot to zero before the event is read, so the amount a
        // blow went PAST lethal is not recoverable from state -- this
        // approximates it from the weapon and the target, which is enough
        // to choose between falling and being thrown, and to size how hard
        // the death beat snaps away from the hit.
        const blow = lastBlow.get(a) ?? 0;
        const overkill = blow / Math.max(1, def.hp);
        // A-22 + size-scaled destruction: the debris, shockwave and shake
        // all scale with the body, so a boss dying reads nothing like a
        // skitterer popping. Debris sprays along the shot line now, from
        // the impact point, not in a ring from the body's centre.
        scene3d!.fx.death(s.eX[a], s.eY[a], def.radius,
          def.isBoss ? 0xffd76b : ENEMY_TINT[kindIdx]!,
          def.isBoss ? 0x7a4b00 : ENEMY_DARK[kindIdx]!, ddx, ddy);
        // The BODY itself is blown back, not deleted. Only the instanced
        // horde tiers have geometry to hand over; the big skinned actors
        // get their own death beat instead (killActor, below).
        if (defIdx <= 2) {
          // Signed distance off the shooter's centre line, so a body
          // caught from the side spins away from the side it was hit --
          // kept as the fallback for a splash kill, which has no ddx/ddy.
          const offAxis = s.eX[a] - s.playerX;
          // The body's RADIUS, not a size. The corpse used to be handed a
          // separately-computed scale that disagreed with the living
          // body's by a factor of three, so a body shrank to a third of
          // itself on the frame it died. The renderer owns body size now
          // (massBodyScale) and both callers read the one function.
          scene3d!.fx.kill(s.eX[a], s.eY[a], defIdx, def.radius,
            def.radius > 0.45 ? 0.8 : 1.15, overkill, offAxis, a, ddx, ddy);
        } else {
          // Elite/boss: hold its actor out of the normal per-frame claim
          // for a death beat instead of letting it vanish the frame the
          // sim frees its pool slot (Contact and Impact, item 2).
          scene3d!.killActor(defIdx, s.eIsBoss[a] === 1, s.eX[a], s.eY[a],
            ddx, ddy, def.radius, def.isBoss, overkill);
        }
        juice.addShake(juiceConfig.screenshake.kill * (def.isBoss ? 6 : def.radius * 1.6 + 0.5));
        if (!def.isBoss) {
          massDeathCount++;
          massDeathSumX += s.eX[a];
          massDeathSumY += s.eY[a];
        }
        if (def.isBoss) {
          juice.addShake(juiceConfig.screenshake.bossSlam);
          juice.addSlowMo(juiceConfig.slowMo.bossDeathMs, juiceConfig.slowMo.bossDeathScale);
          scene3d!.fx.ring(s.eX[a], s.eY[a], 0xffffff, 5.5, 40);
          scene3d!.fx.burst(s.eX[a], s.eY[a], 48, 0xffd76b, 0.28);
          // THE BOSS BECOMES A SPECTACLE: "the shell explodes -- debris,
          // fire, screenshake -- before the body falls" (screenshake and
          // the gold burst/ring above already existed; this adds the
          // debris and fire the shell-shatter read needs). Reuses the same
          // burst primitive with a rock tint and a hot-flame tint, same
          // moment killActor above starts the 0.6s death-beat fall.
          scene3d!.fx.burst(s.eX[a], s.eY[a], 44, 0x8b8f96, 0.4);
          scene3d!.fx.burst(s.eX[a], s.eY[a], 40, 0xff6a1a, 0.46);
          scene3d!.fx.label(s.eX[a], s.eY[a] + 1.5, 'ISLE HELD', 0xffd76b, 40, 0.4);
          audio.bossDeath();
        } else {
          audio.death();
        }
        break;
      }
      case Ev.Breach: {
        // AUDIT A-13: this body was never killed -- it slipped past the
        // squad line (`damageSquad`/`Ev.SquadHit` already charged for the
        // leak, on the same tick). eX/eY are read now, before the sim's
        // NEXT tick can recycle this pool slot, same as Ev.Death above.
        const defIdx = p0;
        const def = s.config.enemies[defIdx]!;
        const tier = Math.min(2, defIdx);
        scene3d!.fx.breach(s.eX[a], s.eY[a], tier, def.radius);
        // Where the leak happened, not just that one did -- the generic
        // squad-hit vignette from damageSquad doesn't say where.
        scene3d!.fx.ring(s.eX[a], s.eY[a], 0xff3b30, 0.9, 16);
        break;
      }
      case Ev.Charged: {
        // The verdict, said in one word at the moment of delivery.
        const tier = a;
        if (tier === 0) {
          scene3d!.fx.label(s.playerX, s.playerY + 3.2, 'FIZZLED', 0xff6a5c, 30, 0.45);
          audio.squadHit();
        } else if (tier === 1) {
          scene3d!.fx.label(s.playerX, s.playerY + 3.2, 'PARTIAL CHARGE', 0xffc24a, 30, 0.45);
          audio.weaponSwap();
        } else {
          juice.addShake(juiceConfig.screenshake.bossSlam * 0.8);
          juice.addSlowMo(300, 0.4);
          scene3d!.fx.label(s.playerX, s.playerY + 3.2, 'FULL CHARGE', 0x7dffc0, 38, 0.5);
          audio.levelUp();
        }
        break;
      }
      case Ev.Ally:
        if (b === 0) {
          // It walks out of the machine. Give it an entrance.
          juice.addShake(juiceConfig.screenshake.gate);
          scene3d!.fx.ring(s.lX[a] ?? s.playerX, s.lY[a] ?? s.playerY, 0xc79aff, 3.6, 26);
          scene3d!.fx.label(s.playerX, s.playerY + 4.0, 'IT FIGHTS FOR YOU', 0xc79aff, 30, 0.5);
          audio.bossIntro();
        } else {
          scene3d!.fx.burst(s.lX[a] ?? s.playerX, s.lY[a] ?? s.playerY, 26, 0x8a6ad8, 0.24);
          audio.death();
        }
        break;
      case Ev.Monolith: {
        // D3: same redirect as Ev.TitanFreed below — kind 1 is banned
        // vocabulary, shown as kind 0 everywhere in the render layer.
        // Kinds 0, 2, 3 are unchanged (four-kind variety preserved).
        const names = ['SAURIAN', 'WAR MECH', 'BULWARK', 'ARTILLERY TITAN'];
        const shownKind = b === 1 ? 0 : b;
        scene3d!.fx.label(s.playerX, s.playerY + 4.4,
          `${names[shownKind] ?? 'TITAN'} ENTOMBED`, 0xffc44a, 30, 0.5);
        audio.bossPhase(0);
        // M7 zoom punch, titan entry (ANIMATION_DOCTRINE.md, spec'd,
        // never built until this pass).
        scene3d!.punchCamera(1.4);
        break;
      }
      case Ev.TitanFreed: {
        const names = ['SAURIAN', 'WAR MECH', 'BULWARK', 'ARTILLERY TITAN'];
        // D3: kind 1 ("WAR MECH") is banned vocabulary (ART_BIBLE prop
        // list) and is redirected to kind 0's mesh at every render site
        // in scene3d.ts (see `renderTitanKind` there — the one place
        // that redirect lives). Kinds 0, 2, 3 keep their own names —
        // four-kind reward variety is unchanged; only the reported kind
        // is redirected. The announcement text has to agree with what
        // the player is actually looking at, so it reads the same
        // redirect rather than a copy of the mapping.
        const shownKind = b === 1 ? 0 : b;
        juice.addShake(juiceConfig.screenshake.bossSlam * 1.2);
        juice.addSlowMo(520, 0.28);
        // M7 zoom punch, stage completion -- bigger than the reveal punch.
        scene3d!.punchCamera(2.0);
        scene3d!.fx.ring(s.mX[a] ?? s.playerX, s.mY[a] ?? s.playerY, 0xffc44a, 7.0, 34);
        scene3d!.fx.burst(s.mX[a] ?? s.playerX, s.mY[a] ?? s.playerY, 54, 0xffe0a0, 0.42);
        scene3d!.fx.label(s.playerX, s.playerY + 4.4,
          `${names[shownKind] ?? 'TITAN'} FREED`, 0xffd76b, 44, 0.55);
        audio.bossDeath();
        break;
      }
      case Ev.ShieldHit:
        // Absorbed for free -- a cyan blink, never the red squad-hit flash.
        scene3d!.fx.hit(s.playerX, s.playerY, 4, 0x5ad7ff);
        break;
      case Ev.Dodge:
        // A-43: bright and forward, distinct from ShieldHit's small blink
        // above -- this is the player's OWN action landing, not an
        // absorption. No shake (permanently retired, commit 7c9f4eb).
        scene3d!.fx.hit(s.playerX, s.playerY, 8, 0x9be7ff);
        scene3d!.fx.burst(s.playerX, s.playerY, 20, 0x9be7ff, 0.22);
        audio.dodge();
        break;
      case Ev.PickedUp:
        audio.pickup();
        if (a === PICKUP_RECRUIT) {
          // A body joining the squad reads as growth, not a gear change --
          // BUILT-THEN-MISROUTED (both kinds used to fall into the same
          // `a !== 0` weapon-swap branch below, since PICKUP_WEAPON=1 and
          // PICKUP_RECRUIT=2 are both truthy). Distinct colour and cue.
          scene3d!.fx.hit(s.playerX, s.playerY, 3, 0x7dffd0);
          scene3d!.fx.burst(s.playerX, s.playerY, 18, 0x7dffd0, 0.12);
          audio.recruit();
        } else {
          // A weapon swap changes how the gun looks and sounds, so the
          // player can tell what they picked up without reading a log.
          scene3d!.fx.hit(s.playerX, s.playerY, 3, 0xffd76b);
          scene3d!.fx.burst(s.playerX, s.playerY, 22, 0xffd76b, 0.14);
          audio.weaponSwap();
        }
        break;
      case Ev.GatePassed: {
        const delta = b;
        // THE +0 LIE (founder audit, 2026-08-20): delta>=0 read as "success"
        // -- green burst, positive buzz, a "+0" label -- for a gate that
        // changed nothing. Two real paths produce this: barrier rammed
        // intact where the HP-pool cost rounds under one body, and a x0.5
        // penalty floored at squad=1 (step.ts's multiply-floor fix) where
        // the squad survives but visibly doesn't move. Measured 10 of 54
        // GatePassed events landing at +0 across an 11-chapter sweep.
        // Nothing changed, so nothing fires -- nothing is the honest cue.
        if (delta === 0) break;
        buzz(delta >= 0 ? 12 : 30);
        juice.addShake(juiceConfig.screenshake.gate);
        scene3d!.fx.burst(s.playerX, s.playerY, Math.min(46, 12 + Math.abs(delta)),
          delta >= 0 ? 0x66ffbb : 0xff6b6b, 0.2);
        scene3d!.fx.ring(s.playerX, s.playerY, delta >= 0 ? 0x66ffbb : 0xff6b6b, 2.6);
        scene3d!.fx.label(s.playerX, s.playerY + 1.6,
          `${delta >= 0 ? '+' : ''}${delta}`, delta >= 0 ? 0x9dffd8 : 0xff9a9a, 34);
        audio.gate(delta >= 0, Math.min(10, Math.abs(delta)));
        break;
      }
      case Ev.GateBreak: {
        // subject = gate idx, p0 = op, p1 = value (constants.ts). A
        // SHOOTABLE panel (barrier/crate/vehicle/ally/armour) broke open
        // under fire — distinct from GatePassed, which is the player
        // walking through a resolved multiplier and has its own cue.
        const lanes = s.config.gateGroups[s.gGroup[a]!]?.lanes.length ?? 1;
        const gx = ((s.gLane[a]! + 0.5) / lanes) * s.config.rules.arenaWidth;
        const gy = s.gY[a]!;
        scene3d!.fx.burst(gx, gy, 26, 0xffd8a0, 0.32);
        scene3d!.fx.ring(gx, gy, 0xffe0a8, 3.2, 20);
        audio.gateBreak();
        break;
      }
      case Ev.Armory:
        // The other half of the binary choice: no bodies, better guns.
        juice.addShake(juiceConfig.screenshake.gate * 1.2);
        juice.addSlowMo(240, 0.35);
        scene3d!.fx.burst(s.playerX, s.playerY, 40, 0xffd76b, 0.26);
        scene3d!.fx.ring(s.playerX, s.playerY, 0xffe9a8, 3.6, 26);
        audio.weaponSwap();
        break;
      case Ev.BarrierDown:
        juice.addShake(juiceConfig.screenshake.bossSlam * 0.55);
        scene3d!.fx.ring(s.playerX, s.playerY + 4, 0xff9a4a, 4.4, 28);
        scene3d!.fx.burst(s.playerX, s.playerY + 4, 34, 0xffb36b, 0.24);
        if (b > 0) scene3d!.fx.label(s.playerX, s.playerY + 2.4, `+${b}`, 0x9dffd8, 34);
        audio.bossPhase(0);
        break;
      case Ev.SquadHit: {
        buzz(18);
        juice.addShake(juiceConfig.screenshake.squadHit);
        // A boss swing (KILL-POWER AUDIT, step.ts: damageSquad(s,
        // unitHp(s) * r.bossSwingKillCount)) can cost 4 soldiers in one
        // hit -- the single most decisive damage source in the run -- but
        // this fired the exact same small flat cue as one soldier's worth
        // of routine breach damage, with no way for the player to feel
        // the difference. `a` carries the raw damage amount (emit(s,
        // Ev.SquadHit, -1, amount, squadSize)); scale by how many
        // soldiers THIS hit cost, same "reaction proportional to cause"
        // rule Hit/Death already follow. No shake added — screenshake is
        // permanently retired (commit 7c9f4eb).
        const soldiersLost = a / unitHp(s);
        if (soldiersLost >= 2) {
          const k = Math.min(soldiersLost, 6);
          scene3d!.fx.hit(s.playerX, s.playerY, 16, 0xff2a2a);
          scene3d!.fx.ring(s.playerX, s.playerY, 0xff2a2a, 3.2 + k * 0.4, 22);
          scene3d!.fx.burst(s.playerX, s.playerY, Math.round(14 * k), 0xff5c5c, 0.3);
          // DIRECTED IMPACT (founder finding, live playtest): a boss swing
          // this decisive read as an isolated burst appearing AT the
          // squad, with no visible connection to the boss that threw it —
          // "a boss attack should have visual destruction aimed and
          // directed at the soldiers," not a self-contained pop at the
          // target end alone. Fires a matching origin burst at the
          // boss's own position (when tracked) plus debris explicitly
          // launched along the boss->squad vector, so the eye reads one
          // continuous attack with two ends, not two unrelated events.
          // BEAST HITS SHARE THE SAME "DECISIVE SWING" THRESHOLD, NOT THE
          // SAME ORIGIN (founder finding, 2026-09-04): gale_colossus
          // (is_beast, contact_damage 55) crosses `soldiersLost >= 2` just
          // as easily as a boss swing, but only `s.bossIdx` is checked
          // above -- `s.bossIdx` is set ONLY for `def.isBoss` (step.ts,
          // spawn), never for a beast, so every beast hit fell through to
          // the undirected squad-only burst this section exists to fix.
          // No sim change needed: `s` already carries every live enemy's
          // position and def index render-side, so the beast is found by
          // scanning for one in contact range here rather than adding a
          // second tracked index to sim state (Iron Rule 1 determinism
          // discipline stays untouched -- this is render-only, same as
          // the boss branch it extends).
          let ox = -1, oy = -1;
          if (s.bossIdx >= 0 && s.eHp[s.bossIdx]! > 0) {
            ox = s.eX[s.bossIdx]!; oy = s.eY[s.bossIdx]!;
          } else {
            for (let i = 0; i < s.eHp.length; i++) {
              if (s.eHp[i]! <= 0) continue;
              if (!s.config.enemies[s.eDefIdx[i]!]?.isBeast) continue;
              const ddx = s.eX[i]! - s.playerX, ddy = s.eY[i]! - s.playerY;
              if (Math.hypot(ddx, ddy) > 3) continue;
              ox = s.eX[i]!; oy = s.eY[i]!;
              break;
            }
          }
          if (ox >= 0) {
            const dx = s.playerX - ox, dy = s.playerY - oy;
            const dist = Math.max(0.001, Math.hypot(dx, dy));
            scene3d!.fx.burst(ox, oy, Math.round(10 * k), 0xffb36b, 0.32);
            scene3d!.fx.debris(ox, oy, Math.round(6 * k), 0xff6a2a, dx / dist, dy / dist, 1.6);
          }
          audio.heavySquadHit(k);
        } else {
          scene3d!.fx.hit(s.playerX, s.playerY, 6, 0xff5c5c);
          audio.squadHit();
        }
        break;
      }
      case Ev.LevelUp:
        audio.levelUp();
        scene3d!.fx.ring(s.playerX, s.playerY, 0x9be7ff, 2.6, 22);
        // I7 (genre-teardown-corridor.md: "slow-mo ramps for level-up and
        // boss death") — boss death already had this via addSlowMo; the
        // mechanism existed and was proven live elsewhere, this event
        // alone never called it. Scaled to a beat this size, not the
        // boss's (juiceConfig.slowMo.bossDeathMs/Scale).
        juice.addSlowMo(240, 0.4);
        break;
      case Ev.Boon: {
        // A-45: fires once the choice window actually resolves -- by a
        // real pick (`boonZone`'s buttons) or, unattended, by the same
        // seeded random draw the game always used. Either way this is the
        // confirmation of which upgrade landed, not the offer itself.
        const u = s.config.upgrades[a];
        scene3d!.fx.burst(s.playerX, s.playerY, 26, 0x9be7ff, 0.2);
        if (u) {
          const magnitude = u.op === 'mul_add' ? `+${Math.round(u.value * 100)}%`
            : u.op === 'mul' ? `x${u.value.toFixed(2)}`
              : `+${u.value}`;
          // Z-ROUTE BUILD PLAN, Priority 7, FULL VERSION (2026-09-04): a
          // prior pass here scaled this down to a bigger world-space label
          // because "no screen-space HUD banner system exists in this
          // codebase" made a true one look like a new-system cost. Built
          // that system now (`#banner` in index.html, outside `#hud` for
          // the same never-mid-animation-cut-off reason `#controls`
          // lives there) -- this is the real reference-matching effect,
          // a screen-filling "ATK+150%" callout, not the smaller
          // world-space stand-in. World-space burst above stays: the
          // banner reads the STAT, the burst reads the LOCATION.
          showBanner(`${u.name.toUpperCase()} ${magnitude}`, 0x9be7ff);
        }
        audio.choicePick();
        break;
      }
      case Ev.BossPhase:
        // A-19: the sim now emits phase crossings, so the roar has
        // something real to fire on.
        juice.addShake(juiceConfig.screenshake.bossSlam * 0.7);
        juice.addSlowMo(260, 0.35);
        scene3d!.fx.ring(s.eX[s.bossIdx] ?? s.playerX, s.eY[s.bossIdx] ?? s.playerY, 0xff7a3d, 4.2, 30);
        scene3d!.fx.label(s.playerX, s.playerY + 3.4, a >= 2 ? 'ENRAGED' : 'PHASE 2', 0xff8a4a, 30, 0.5);
        audio.bossPhase(a);
        break;
      case Ev.Spawned: {
        // L2 spawn grow-in: every body is born small under the fog veil
        // and grows in over 0.35s — a column marching out of the haze,
        // never a wall materializing mid-view.
        scene3d!.enemyGrow[a] = 21;
        const def = s.config.enemies[b]!;
        if (def.hp >= 400 && !def.isBoss) {
          // Elites announce themselves — a heavy that arrives silently
          // reads as just another body in the crowd.
          juice.addShake(juiceConfig.screenshake.gate * 0.7);
          scene3d!.fx.ring(s.eX[a], s.eY[a], 0xff7a3d, 3.0, 24);
          audio.bossPhase(0);
        }
        if (def.isBoss && !bossAnnounced) {
          bossAnnounced = true;
          clashCrowdAtSpawn = s.squadSize;
          juice.addShake(juiceConfig.screenshake.bossSlam);
          juice.addSlowMo(420, 0.3);
          scene3d!.fx.label(s.playerX, s.playerY + 4, 'STORMHULK', 0xffd76b, 46, 0.35);
          audio.bossIntro();
        }
        break;
      }
      default: break;
    }
  }
  const md = juiceConfig.massDeath;
  if (massDeathCount >= md.threshold) {
    // The one shared moment for the whole batch, at its centroid. No
    // shake -- screenshake is permanently retired (commit 7c9f4eb,
    // "at our density it read as instability, not impact").
    const cx = massDeathSumX / massDeathCount, cy = massDeathSumY / massDeathCount;
    const scale = Math.min(massDeathCount / md.threshold, md.maxScale);
    scene3d!.fx.ring(cx, cy, 0xffe9b0, 3.0 + scale * 2.0, 28);
    scene3d!.fx.burst(cx, cy, Math.round(md.particleCount * scale), 0xffe9b0, 0.22);
    audio.massKill(massDeathCount);
  }
}

/**
 * Rebuilding the HUD every frame destroys and recreates its buttons 60
 * times a second, so a tap can never land on one — the element is
 * detached before the gesture completes. That is what made the game
 * freeze forever on the first level-up. The HUD is now rebuilt only when
 * its content actually changes, and while a choice or the death screen is
 * up the key deliberately excludes the clock so the buttons stay mounted.
 */
let hudKey = '';

/**
 * INSTALL PROMPT (AUDIT A-31). Without capturing this, Android falls back
 * to Chrome's own mini-infobar, which converts far worse than an in-app
 * button, and iOS Safari never prompts at all -- the PWA was installable
 * and almost nobody was installing it.
 *
 * `beforeinstallprompt` is Chromium-only and can fire before or after the
 * start screen first renders, so it is captured at module scope (not
 * inside `main()`) and bumps `hudKey` to force a repaint if the button
 * needs to appear on an already-visible start screen.
 */
let deferredInstallPrompt: Event & { prompt(): Promise<void> } | null = null;
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  deferredInstallPrompt = e as typeof deferredInstallPrompt;
  if (hudKey.startsWith('start:')) hudKey = '';
});
window.addEventListener('appinstalled', () => {
  deferredInstallPrompt = null;
  if (hudKey.startsWith('start:')) hudKey = '';
});
/** True once installed as a standalone app, on any platform. */
const isStandalonePwa = (): boolean =>
  window.matchMedia('(display-mode: standalone)').matches
  || (navigator as unknown as { standalone?: boolean }).standalone === true;
/** iOS Safari never fires beforeinstallprompt -- it needs the Share-sheet hint instead. */
const isIosSafari = /iphone|ipad|ipod/i.test(navigator.userAgent)
  && !('MSStream' in window);

function drawHud(s: RunState, now: number): void {
  if (isLandscape) {
    if (hudKey === 'landscape') return;
    let html = '';
    html += `
      <div style="position:absolute;inset:0;background:rgba(6,22,42,.92);display:flex;
                  flex-direction:column;align-items:center;justify-content:center;gap:14px">
        <div style="font-size:44px">⟳</div>
        <div style="font-size:20px;font-weight:900;letter-spacing:.16em">ROTATE YOUR DEVICE</div>
        <div style="opacity:.8;font-size:14px">Bonwarsy is played in portrait</div>
      </div>`;
    hud.innerHTML = html;
    hudKey = 'landscape';
    return;
  }

  const boss = s.bossIdx >= 0 && s.ep.alive[s.bossIdx]
    ? s.eHp[s.bossIdx] / s.config.enemies[s.eDefIdx[s.bossIdx]]!.hp
    : -1;
  // Explicit destination. Top War's stages show WAVE n/25 and gate the
  // region behind a boss; without a counter and a distance-to-boss bar a
  // run is just noise with no sense of travelling toward anything.
  const totalWaves = s.config.waves.length;
  const waveNo = Math.min(totalWaves, s.waveCursor + 1);
  const bossTick = s.config.waves[totalWaves - 1]?.startTick ?? 1;
  const journey = Math.min(100, (s.tick / bossTick) * 100);
  const bossLive = s.bossIdx >= 0 && s.ep.alive[s.bossIdx];
  const bossNear = !bossLive && s.tick > bossTick - 60 * 12 && s.tick < bossTick;

  // ONE TOP-BAND ROW (D6 -- kept for the collision-avoidance reasoning,
  // shrunk to two elements per the isle-12 legibility pass, 2026-08-22).
  // SQUAD, WAVE/THREAT/objective and ARMAMENT (LV/elapsed/felled, wave
  // count, difficulty multiplier, weapon name, stream count) were eight
  // live numbers in one glance -- founder verdict: cut to the two that
  // answer what the player is actually tracking mid-run, "how many do I
  // have" and "how far to the end". Everything else (run stats, weapon
  // identity) belongs on the end screen, where the player has a second to
  // read it, not stacked in front of the horde.
  let html = `
    <div style="position:absolute;top:calc(5px + env(safe-area-inset-top));
                left:calc(10px + env(safe-area-inset-left));
                right:calc(108px + env(safe-area-inset-right));
                display:flex;align-items:center;gap:10px">
      <div class="plate" data-hud="squad" style="flex:0 0 auto;line-height:1.1;
                  padding:6px 12px;overflow:hidden">
        <div class="hud-label" style="font-size:8.5px">SQUAD</div>
        <div style="font-size:24px;font-weight:900;letter-spacing:-.02em;margin-top:1px;
                    white-space:nowrap;overflow:hidden;text-overflow:clip;
                    background:linear-gradient(180deg,#ffffff 30%,#8fe0ff);
                    -webkit-background-clip:text;background-clip:text;
                    -webkit-text-fill-color:transparent;text-shadow:none;
                    filter:drop-shadow(0 2px 7px rgba(90,215,255,.45))">${s.squadSize}${
                      s.variant === 1 ? `<span style="font-size:13px;opacity:.65;font-weight:800">/${s.clashThreshold}</span>` : ''
                    }</div>
      </div>
      <div class="plate" data-hud="journey" style="flex:1 1 auto;min-width:0;
                  padding:6px 16px 6px 12px;overflow:visible">
        <!-- End marker sits OUTSIDE .bar's own overflow:hidden, in this
             wrapper's padding, so it never gets clipped at journey=100%. -->
        <div style="position:relative">
          <div class="bar" style="height:7px">
            <i style="width:${journey}%;background:linear-gradient(90deg,#5ad7ff,#7dffd0,#ffd76b)"></i>
          </div>
          <div aria-label="finish" style="position:absolute;right:0;top:50%;
                      transform:translate(50%,-50%);width:11px;height:11px;border-radius:50%;
                      background:#ffd76b;border:2px solid rgba(7,18,36,.92);
                      box-shadow:0 0 7px rgba(255,215,107,.75)"></div>
        </div>
      </div>
    </div>`;

  // Start card. Nothing runs before the player commits: it unlocks audio
  // from a real gesture (so the opening seconds are not silent), stops the
  // clock running while the page is still painting, and gives the best
  // score somewhere to live BEFORE the run rather than only after it.
  if (!started) {
    // Guard BEFORE writing: an unguarded write here rebuilds the button
    // 60x/sec and a tap can never land on it — exactly the detach bug
    // that froze the level-up cards. Any early-return HUD branch needs
    // this check first, not after.
    const installState = deferredInstallPrompt ? 'a' : (isIosSafari && !isStandalonePwa()) ? 'i' : '0';
    if (hudKey === `start:${progress.unlocked}:${chapter}:${progress.character}:${installState}`) return;
    // LEVEL SELECT. Eleven isles, each a row in chapters.csv, unlocked by
    // clearing the one before it. Every card shows the two things that
    // actually differ between levels -- the pace and the guns it hands out
    // -- so picking one is an informed choice rather than a number.
    const charCards = cfg.characters.map((c, i) => {
      const sel = i === progress.character;
      const hex = `#${c.tint.toString(16).padStart(6, '0')}`;
      return `<button class="chr" data-chr="${i}"
        style="pointer-events:auto;cursor:pointer;text-align:left;border-radius:12px;
               padding:8px 10px;border:2px solid ${sel ? hex : 'rgba(170,205,240,.3)'};
               background:${sel ? 'rgba(16,42,78,.92)' : 'rgba(12,28,52,.6)'};
               color:#fff;-webkit-appearance:none">
        <div style="display:flex;align-items:center;gap:6px">
          <span style="width:10px;height:10px;border-radius:50%;background:${hex};
                display:inline-block;box-shadow:0 0 8px ${hex}"></span>
          <span style="font:900 13px/1 ui-sans-serif,system-ui">${c.name.toUpperCase()}</span>
        </div>
        <div style="font:600 9px/1.35 ui-sans-serif,system-ui;opacity:.8;margin-top:4px">${c.tagline}</div>
      </button>`;
    }).join('');
    const gunChips = cfg.raw.weapons.map((w) => `
      <span style="font:800 9px/1 ui-sans-serif,system-ui;letter-spacing:.05em;
             padding:5px 8px;border-radius:9px;border:1px solid rgba(170,205,240,.35);
             background:rgba(12,30,56,.6);color:#cfe6ff">${w.name.toUpperCase()}</span>`).join('');
    const cards = CHAPTERS.map((c) => {
      // Two different closed states, and the card must not conflate them: an
      // isle you have not EARNED yet, and an isle that is not FINISHED yet.
      // The second is ours to admit, not the player's to puzzle over.
      const shipped = SHIPPED_ISLES.has(c.chapter);
      // Shipped isles are not a progression chain (isle 12 is a separate
      // authored isle, not "the level after 11 unwinnable ones") -- both
      // open from a cold load. `progress.unlocked` is still recorded (see
      // recordRun) in case a real unlock chain returns with 2-11.
      const open = shipped;
      // A withheld isle cannot also read as HELD. Saved progress from before
      // the lock can carry clears for isles 2-11, and showing a green tick on
      // a card labelled BUILDING claims the player finished something we are
      // admitting is unfinished. Stored progress is kept untouched so it
      // returns intact when SHIPPED_ISLES grows; only the display is gated.
      const done = shipped && progress.cleared.includes(c.chapter);
      const starsEarned = progress.stars[c.chapter] ?? 0;
      const heat = Math.round(((c.chapter - 1) / Math.max(1, CHAPTERS.length - 1)) * 100);
      return `<button class="lvl" data-ch="${c.chapter}" ${open ? '' : 'disabled'}
        style="position:relative;pointer-events:auto;cursor:${open ? 'pointer' : 'default'};
               border-radius:14px;padding:9px 4px 8px;border:2px solid ${done ? 'rgba(120,255,190,.75)' : open ? 'rgba(180,225,255,.5)' : 'rgba(150,175,200,.22)'};
               background:${open ? `linear-gradient(160deg,rgba(${40 + heat},${120 - heat / 2},${180 - heat},.85),rgba(10,30,58,.9))` : 'rgba(12,26,46,.55)'};
               color:${open ? '#fff' : 'rgba(190,210,235,.4)'};font:900 17px/1 ui-sans-serif,system-ui,sans-serif;
               -webkit-appearance:none">
        ${open ? c.chapter : shipped ? '\u{1F512}' : '\u{1F6A7}'}
        <div style="font:800 8px/1.3 ui-sans-serif,system-ui;letter-spacing:.06em;opacity:.8;margin-top:4px">
          ${open
            ? `${Math.floor((CHAPTER_SECS.get(c.chapter) ?? 0) / 60)}:${String((CHAPTER_SECS.get(c.chapter) ?? 0) % 60).padStart(2, '0')}`
            : shipped ? '&nbsp;' : 'BUILDING'}
        </div>
        ${done
          ? `<div style="margin-top:2px;font-size:9px;letter-spacing:.1em;color:#ffd76b">${'\u2605'.repeat(starsEarned)}${'\u2606'.repeat(3 - starsEarned)}</div>`
          : ''}
      </button>`;
    }).join('');
    // SCROLLABLE START SCREEN. This layout needs ~719px of height, and it
    // used to be unreachable below that -- on a 375x560 viewport not one
    // pixel of PLAY was hittable, so the game could not be started at all
    // and said nothing about why. That is the browser-visit path: a social
    // link opened in Safari with its toolbars up is exactly this case.
    //
    // `overflow:auto` was already here and was inert, for three reasons that
    // all had to be fixed:
    //   1. #hud is pointer-events:none, which the scroller inherited, so no
    //      touch or wheel event ever reached it to scroll with.
    //   2. body sets touch-action:none, which kills touch panning in every
    //      descendant. The scroller has to opt back in with pan-y.
    //   3. justify-content:center on an overflowing flex column overflows in
    //      BOTH directions, and the top overflow is unreachable by scrolling
    //      even once scrolling works. An inner wrapper with `margin:auto 0`
    //      centres while there is slack and collapses to 0 when there is
    //      not, which is the behaviour actually wanted.
    hud.innerHTML = `
      <div style="position:absolute;inset:0;display:flex;flex-direction:column;
                  align-items:center;overflow-y:auto;overscroll-behavior:contain;
                  -webkit-overflow-scrolling:touch;pointer-events:auto;touch-action:pan-y;
                  padding:calc(18px + env(safe-area-inset-top)) 16px calc(18px + env(safe-area-inset-bottom));
                  background:linear-gradient(180deg,rgba(20,60,110,.55),rgba(8,28,58,.88))">
       <div style="margin:auto 0;display:flex;flex-direction:column;
                   align-items:center;gap:6px;width:100%">
        <div style="font-size:12px;letter-spacing:.42em;opacity:.85">SKY ARCHIPELAGO</div>
        <div style="font-size:38px;font-weight:900;letter-spacing:-.01em;line-height:1">BONWARSY</div>
        ${progress.runs > 0 ? `
          <div style="margin-top:2px;font-size:12px;opacity:.85">
            best <b>${progress.best}</b> felled &middot; ${progress.cleared.filter((n) => SHIPPED_ISLES.has(n)).length}/${SHIPPED_ISLES.size} isles held
          </div>` : `
          <div style="margin-top:2px;opacity:.85;font-size:12px;text-align:center;line-height:1.6">
            Hold the corridor.<br>Drag to move &mdash; firing is automatic.
          </div>`}
        <div style="margin-top:10px;width:100%;max-width:330px">
          <div style="font-size:10px;letter-spacing:.3em;opacity:.7;margin-bottom:6px">CHOOSE YOUR LEADER</div>
          <div style="display:grid;grid-template-columns:repeat(2,1fr);gap:7px">${charCards}</div>
        </div>
        <div style="margin-top:12px;display:grid;grid-template-columns:repeat(5,1fr);
                    gap:7px;width:100%;max-width:330px">${cards}</div>
        ${SHIPPED_ISLES.size < CHAPTERS.length ? (() => {
          // Isle 12 shipping alongside isle 1 (2026-08-22) made the shipped
          // set non-contiguous, so "still building" is now a plain
          // membership filter, not a range past some cutoff -- the withheld
          // batch (isles 2-11, documented-corrupt, see SHIPPED_ISLES's own
          // comment) happens to still be one contiguous block itself, so the
          // lo-hi range summary below stays accurate without special-casing
          // whichever isle a debug `?chapter=N` load happens to be on.
          const stillBuilding = CHAPTERS.filter((c) => !SHIPPED_ISLES.has(c.chapter));
          if (!stillBuilding.length) return '';
          const lo = stillBuilding[0]!.chapter, hi = stillBuilding[stillBuilding.length - 1]!.chapter;
          const range = lo === hi ? `Isle ${lo} is` : `Isles ${lo}&ndash;${hi} are`;
          const shippedList = [...SHIPPED_ISLES].sort((a, b) => a - b)
            .map((n) => `isle ${n}`).join(' and ');
          return `
        <div style="margin-top:7px;width:100%;max-width:330px;font:600 10px/1.5 ui-sans-serif,system-ui;
                    opacity:.62;text-align:center;color:#cfe6ff">
          \u{1F6A7} ${range} still being built.
          ${shippedList.charAt(0).toUpperCase()}${shippedList.slice(1)} ${SHIPPED_ISLES.size === 1 ? 'is' : 'are'} the finished ones &mdash; play them.
        </div>`;
        })() : ''}
        <div style="margin-top:10px;width:100%;max-width:330px">
          <div style="font-size:10px;letter-spacing:.3em;opacity:.7;margin-bottom:5px">ARSENAL — WON IN BATTLE, CRATE BY CRATE</div>
          <div style="display:flex;flex-wrap:wrap;gap:4px">${gunChips}</div>
        </div>
        <div style="margin-top:10px;font-size:11px;opacity:.6;letter-spacing:.1em">
          ${SHIPPED_ISLES.size < CHAPTERS.length
            ? 'NINETEEN WAVES &middot; HOLD TO THE FINALE'
            : 'EACH ISLE IS FASTER, TOUGHER, BETTER ARMED'}
        </div>
        <div style="margin-top:12px;font-size:11px;opacity:.55;letter-spacing:.08em">TAP AN ISLE TO PLAY</div>
        ${deferredInstallPrompt ? `
        <button id="install-btn" style="pointer-events:auto;cursor:pointer;margin-top:14px;
               padding:9px 20px;border-radius:22px;border:1px solid rgba(170,205,240,.4);
               background:rgba(16,42,78,.92);color:#fff;-webkit-appearance:none;
               font:800 12px/1 ui-sans-serif,system-ui;letter-spacing:.06em">
          ⬇ INSTALL APP
        </button>` : isIosSafari && !isStandalonePwa() ? `
        <div style="margin-top:14px;font-size:11px;opacity:.7;text-align:center;line-height:1.5">
          Add to your home screen: tap <b>Share</b> ↑, then <b>Add to Home Screen</b>
        </div>` : ''}
       </div>
      </div>`;
    hudKey = `start:${progress.unlocked}:${chapter}:${progress.character}:${installState}`;
    return;
  }

  if (awaitingResume) {
    html += `
      <div style="position:absolute;inset:0;background:rgba(6,22,42,.86);display:flex;
                  flex-direction:column;align-items:center;justify-content:center;gap:16px">
        <div style="font-size:13px;letter-spacing:.34em;opacity:.85">PAUSED</div>
        <button class="card" id="resume" style="width:auto;padding:18px 52px;font-size:20px;
                font-weight:900;text-align:center;background:linear-gradient(160deg,#5ad7ff,#2f9fd0);
                color:#04121f;border-color:rgba(255,255,255,.5)">TAP TO RESUME</button>
        <button class="card" id="restart" style="width:auto;padding:13px 38px;font-size:15px;
                font-weight:800;text-align:center;background:rgba(10,32,60,.6);
                color:#cfe6ff;border-color:rgba(160,220,255,.45)">RESTART ISLE</button>
        <button class="card" id="menu" style="width:auto;padding:13px 38px;font-size:15px;
                font-weight:800;text-align:center;background:rgba(10,32,60,.6);
                color:#cfe6ff;border-color:rgba(160,220,255,.45)">EXIT TO MENU</button>
        <div style="font-size:11px;opacity:.6;letter-spacing:.1em">this run will be abandoned</div>
      </div>`;
    if (hudKey === 'resume') return;
    hud.innerHTML = html;
    hudKey = 'resume';
    return;
  }

  // INTEGRATION FIX (founder verdict on 44e71d97): the tip-banner
  // mechanism (TIP_SECONDS, previous commit) is deleted outright, not
  // time-limited — see the note beside `awaitingResume` near the top of
  // this file for why. A flickering banner (the entombed-titan event
  // re-triggers `tipText` every time it fires, and multiple titans can
  // entomb inside one 8s window) is worse than a persistent one.

  // ISLE COMPLETE BEAT: on a boss kill specifically, hold the live scene
  // (the corpse, the corridor just cleared) instead of cutting straight to
  // the dark stats screen -- a light, legible overlay, not a blank one.
  // Death and timeout are untouched: bossKilledAt only ever gets set for
  // Outcome.BossKilled, so they fall straight through to the existing
  // screen below exactly as they did before this beat existed.
  if (s.phase === Phase.Ended && s.outcome === Outcome.BossKilled
      && bossKilledAt >= 0 && now - bossKilledAt < ISLE_COMPLETE_BEAT_MS) {
    if (hudKey !== 'isle-complete') {
      hudKey = 'isle-complete';
      hud.innerHTML = `
        <div style="position:absolute;inset:0;display:flex;align-items:center;
                    justify-content:center;pointer-events:none">
          <div style="background:rgba(4,10,20,.5);padding:20px 40px;border-radius:16px;
                      border:1px solid rgba(255,215,107,.55);text-align:center">
            <div style="font-size:40px;font-weight:900;letter-spacing:.1em;color:#ffd76b;
                        text-shadow:0 2px 14px rgba(0,0,0,.7)">ISLE COMPLETE</div>
          </div>
        </div>`;
    }
    return;
  }

  if (s.phase === Phase.Ended) {
    const won = s.outcome === Outcome.BossKilled;
    // Isle taken or contested (ISLE-12-SPEC.md's own framing: stars decide
    // how much of the isle is held, not whether the run counts). Reads
    // clashCrowdAtSpawn -- captured client-side at the boss's own Spawned
    // event, since RunState's clashCrowd/stars are variant-1-only (the
    // offline threshold sim) and every real player runs variant 0.
    const starsThisRun = won ? starsFor(clashCrowdAtSpawn) : 0;
    const clashLine = clashCrowdAtSpawn >= 0
      ? `<div style="font-size:16px;font-weight:800;letter-spacing:.04em;margin-top:2px">
           CROWD ${clashCrowdAtSpawn} <span style="opacity:.6">vs</span> THRESHOLD ${Math.round(cfg.rules.clashThresholdT)}
         </div>
         <div style="font-size:22px;letter-spacing:.3em;color:#ffd76b;margin-top:2px">
           ${'★'.repeat(starsThisRun)}${'☆'.repeat(3 - starsThisRun)}
         </div>`
      : '';
    const title = won ? 'ISLE HELD'
      : s.lostToBoss ? 'THE STORMHULK REACHED YOU'
      : s.outcome === Outcome.Died ? 'OVERRUN' : 'RUN OVER';
    html += `
      <div style="position:absolute;inset:0;background:rgba(4,8,18,.86);
                  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px">
        <div style="font-size:${s.lostToBoss ? 30 : 42}px;font-weight:900;letter-spacing:.04em;
                    color:${won ? '#ffd76b' : '#ff8a7a'}">${title}</div>
        ${clashLine}
        <div style="margin:14px 0 22px;text-align:center;line-height:1.9;font-size:15px">
          <div style="font-size:52px;font-weight:900;line-height:1">${s.killCount}</div>
          <div style="opacity:.75;letter-spacing:.2em;font-size:12px;margin-bottom:10px">FELLED</div>
          ${s.lostToBoss ? `<div style="opacity:.85;font-size:13px;margin-bottom:8px;max-width:30ch">It never stops walking. Pour everything into it before it arrives.</div>` : ''}
          ${beatBest
            ? `<div style="color:#ffd76b;font-weight:900;letter-spacing:.14em">★ NEW BEST ★</div>`
            : `<div style="opacity:.8">best ${progress.best}</div>`}
          <div style="opacity:.75;font-size:13px;margin-top:8px">
            ${(s.tick / TICK_RATE).toFixed(1)}s · peak squad ${s.squadPeak} · run ${progress.runs}
          </div>
        </div>
        <button class="card" id="retry" style="width:auto;padding:18px 52px;font-size:20px;font-weight:900;
                text-align:center;background:linear-gradient(160deg,#5ad7ff,#2f9fd0);color:#04121f;
                border-color:rgba(255,255,255,.5)">RETRY</button>
        <button class="card" id="menu" style="margin-top:10px;width:auto;padding:13px 38px;
                font-size:15px;font-weight:800;text-align:center;background:rgba(10,32,60,.6);
                color:#cfe6ff;border-color:rgba(160,220,255,.45)">NEXT</button>
      </div>`;
  }

  const key = s.phase === Phase.Running
    ? `r|${s.squadSize}|${s.level}|${Math.floor(s.tick / 6)}|${s.weaponIdx}|${hasTouched}|${muted}|${waveNo}|${Math.round(journey)}|${difficultyTier(s).toFixed(1)}|${bossLive}|${bossNear}|${boss >= 0 ? Math.round(boss * 200) : -1}`
    : `${s.phase}|${s.outcome}|${s.level}|${progress.runs}|${beatBest}`;
  if (key === hudKey) return;
  hudKey = key;
  hud.innerHTML = html;
}

/** Short haptic pulse where the platform supports it. */
function buzz(ms: number): void {
  // Deliberately NOT gated on `muted`: muting on a bus should not also
  // kill haptics. (Unsupported on iOS Safari by construction.)
  try { navigator.vibrate?.(ms); } catch { /* unsupported */ }
}

/**
 * 0-3 stars from a crowd reading, against THIS isle's authored thresholds
 * (levels.csv t_threshold/star2/star3, loaded into cfg.rules by config.ts
 * -- the exact bands step.ts's variant-1 sim already uses at s.tick 1690,
 * just applied here to a real run's crowd-at-clash instead of a synthetic
 * one). -1 (no clash reached) scores 0, same as below every threshold.
 */
function starsFor(crowd: number): number {
  if (crowd < 0) return 0;
  const r = cfg.rules;
  return crowd >= r.star3Threshold ? 3 : crowd >= r.star2Threshold ? 2 : crowd >= r.clashThresholdT ? 1 : 0;
}

function recordRun(s: RunState): void {
  if (recorded) return;
  recorded = true;
  beatBest = s.killCount > progress.best;
  const taken = s.outcome === Outcome.BossKilled;
  // Territory is never taken back for a worse later attempt -- same rule
  // `cleared` already follows, extended to the star count itself.
  const starsThisRun = taken ? starsFor(clashCrowdAtSpawn) : 0;
  progress = {
    best: Math.max(progress.best, s.killCount),
    runs: progress.runs + 1,
    bestSquad: Math.max(progress.bestSquad, s.squadPeak),
    bossKills: progress.bossKills + (taken ? 1 : 0),
    character: progress.character,
    muted, taught: progress.taught,
    // Clearing a level unlocks the next one. Progression is the only
    // reason to come back to a level select at all.
    unlocked: taken
      ? Math.max(progress.unlocked, Math.min(CHAPTERS.length, chapter + 1))
      : progress.unlocked,
    cleared: taken && !progress.cleared.includes(chapter)
      ? [...progress.cleared, chapter]
      : progress.cleared,
    stars: taken
      ? { ...progress.stars, [chapter]: Math.max(progress.stars[chapter] ?? 0, starsThisRun) }
      : progress.stars,
  };
  saveProgress(progress);
}

function frame(now: number): void {
  const dt = Math.min(100, now - last);
  last = now;
  audio.setClock(now);

  const scale = juice.update(dt);

  // THE GATE. Boss death (or any Outcome) sets state.phase = Phase.Ended
  // in the same tick, synchronously (step.ts, section 16 "end
  // conditions") -- step.ts's OWN entry point also refuses to do
  // anything once Ended (`if (s.phase === Phase.Ended) return;`, top of
  // stepRun), so the run is double-gated: this boolean stops NEW ticks
  // from being requested, and stepRun would no-op even if one slipped
  // through. Named explicitly (not left as an inline compound condition)
  // so "does the sim advance this frame" is one answerable question, not
  // a boolean buried in an if-statement. `scene3d.render()` below is NOT
  // gated by this -- the scene keeps drawing the frozen state every
  // frame regardless of phase, which is what lets ISLE COMPLETE show the
  // held corridor instead of a blank screen.
  const simIsLive = state.phase === Phase.Running && started && !awaitingResume && !isLandscape;
  if (simIsLive) {
    acc += dt * scale;
    let guard = 0;
    while (acc >= STEP_MS && state.phase === Phase.Running && guard++ < 8) {
      const before = state.fireCooldown;
      // DRAG-FOLLOW. The squad tracks the finger's displacement 1:1 across
      // the corridor. Lanes are still where the horde spawns, but they no
      // longer quantise the player: flooring the thumb position into a
      // lane index is what made this read as tap-to-teleport rather than
      // as a swipe.
      const t = input.targetX(state.playerX / cfg.rules.arenaWidth);
      let frame: TickInput = input.read();
      if (BOT_MODE) {
        // AUTOPILOT (dev-only, ?bot=1): the reference player's lane
        // scoring driving the game's own input pipeline at sim precision.
        // Exists because the capture harness's keyboard actuation cannot
        // pass the squad-1 opening a 60Hz follow-input player clears 63%
        // of the time — evidence frames need a run that is actually being
        // PLAYED. Never active without the query param; ships inert.
        frame = followInput(cfg.rules.followSoftZone, state.playerX, botTargetX());
      } else if (t !== null) {
        frame = followInput(cfg.rules.followSoftZone, state.playerX,
          t * cfg.rules.arenaWidth);
      } else if (frame.mag > 0) {
        // Keyboard: left/right are lane STEPS, not a nudge -- and they
        // step in SCREEN space, so they carry the same mirror as the drag.
        const here = laneOf(cfg.rules.arenaWidth, lanes, state.playerX);
        const step = frame.dir === 128 ? -SCREEN_X_SIGN : frame.dir === 0 ? SCREEN_X_SIGN : 0;
        const want = here + step;
        frame = laneInput(cfg.rules.arenaWidth, lanes, state.playerX,
          Math.min(lanes - 1, Math.max(0, want)));
      }
      // Captured immediately before the tick it precedes -- by the time
      // this loop exits, `prevSnap` holds exactly the tick before
      // `state`'s current one, regardless of how many ticks fired this
      // frame (fix C: fixed-timestep interpolation).
      syncPrevSnap();
      stepRun(state, frame);
      if (state.fireCooldown > before) {
        juice.addShake(juiceConfig.screenshake.shot);
        juice.recoil = juiceConfig.recoil.kickPixels;
        // A-21: muzzle flash and tracer, oriented along the actual shot.
        let ang = 0;
        for (let p = 0; p < state.pp.cap; p++) {
          if (!state.pp.alive[p]) continue;
          if (state.pLife[p] < state.config.weapons[state.weaponIdx]!.projectileLifetimeTicks) continue;
          ang = Math.atan2(-state.pVy[p], state.pVx[p]) + Math.PI / 2;
          break;
        }
        // Real muzzle flares at the barrels, scaled to how many streams
        // actually fired, plus a light pulse that hits the squad.
        scene3d!.flash(
          volleySize(state.squadSize, state.statProjectiles, cfg.rules.maxProjectilesPerVolley));
        void ang;
        audio.shoot();
      }
      consumeEvents(state);
      acc -= STEP_MS;
    }
  } else {
    acc = 0; syncPrevSnap();
    if (state.phase === Phase.Ended) {
      recordRun(state);
      if (state.outcome === Outcome.BossKilled && bossKilledAt < 0) bossKilledAt = now;
      paintControls();
    }
  }

  juice.updateZoom(state.ep.count);
  // Danger is ABSOLUTE, not relative to your best. Measuring against the
  // high-water mark meant dropping from 150 to 35 — still a winning squad
  // — painted a full-screen red panic wash, while a player who never grew
  // got no warning at all while actually dying.
  const DANGER_FLOOR = 12;
  juice.updateNearDeath(
    Math.min(1, state.squadSize / DANGER_FLOOR),
    now, () => { audio.heartbeat(); buzz(24); },
  );
  scene3d?.setDanger(juice.vignette, juice.desaturate);

  if (state.phase === Phase.Ended && input.consume('r')) newRun(seed + 1);

  // Follow-through: the squad leans into lateral travel and settles.
  const dxLean = state.playerX - lastPX;
  lastPX = state.playerX;
  lean += (Math.max(-0.5, Math.min(0.5, dxLean * 1.6)) - lean) * 0.2;
  // FIXED-TIMESTEP INTERPOLATION (fix C, 2026-09-05): `acc` is whatever
  // fraction of a tick is left over after the loop above -- clamped, not
  // extrapolated, in case a long frame ever overshoots the accumulator.
  // A zero-tick frame still advances `alpha` (acc keeps growing without
  // a new tick landing), which is what removes the frozen frame, not a
  // bug in this clamp.
  const alpha = Math.max(0, Math.min(1, acc / STEP_MS));
  lastAlpha = alpha;
  scene3d?.render(state, dt, lean, alpha, prevSnap);
  drawHud(state, now);
  requestAnimationFrame(frame);
}

async function main(): Promise<void> {
  newRun(1);
  // Sky varies with the chosen leader, so a new run looks new.
  scene3d = await createScene(host, cfg.rules.arenaWidth, lanes,
    progress.character);
  input = new Input(scene3d.domElement as unknown as HTMLElement, cfg.dirTable,
    cfg.rules.dragGain, SCREEN_X_SIGN);

  controls.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('#mute')) {
      muted = !muted;
      audio.setMuted(muted);
      progress = { ...progress, muted };
      saveProgress(progress);
      audio.resume();
      paintControls();
      return;
    }
    if (t.closest('#pause')) {
      if (started && state.phase === Phase.Running) { awaitingResume = true; hudKey = ''; }
      audio.resume();
      paintControls();
    }
  });

  hud.addEventListener('click', (e) => {
    const t = e.target as HTMLElement;
    if (t.closest('#install-btn')) {
      // One-shot by spec: a captured prompt can only be shown once, so it
      // is cleared here regardless of the outcome rather than left around
      // to be re-shown stale on the next click.
      const p = deferredInstallPrompt;
      deferredInstallPrompt = null;
      hudKey = '';
      void p?.prompt();
      return;
    }
    if (t.closest('#resume')) {
      awaitingResume = false;
      last = performance.now();
      acc = 0; syncPrevSnap();
      hudKey = '';
      paintControls();
      return;
    }
    const chr = t.closest('.chr') as HTMLElement | null;
    if (chr) {
      const idx = Number(chr.dataset['chr'] ?? 0);
      if (idx !== progress.character) {
        progress = { ...progress, character: idx };
        saveProgress(progress);
        // Re-init so the power set applies from tick zero, not mid-run.
        newRun(seed + 1);
      }
      hudKey = '';
      audio.resume();
      return;
    }
    const lvl = t.closest('.lvl') as HTMLElement | null;
    if (lvl && !(lvl as HTMLButtonElement).disabled) {
      // Tapping a card starts that isle directly -- no separate PLAY step.
      const ch = Number(lvl.dataset['ch'] ?? 1);
      if (ch !== chapter) newRun(seed + 1, ch);
      started = true;
      last = performance.now();
      acc = 0; syncPrevSnap();
      hudKey = '';
      audio.resume();
      paintControls();
      return;
    }
    if (t.closest('#restart')) {
      // Same isle, fresh seed, straight back in.
      newRun(seed + 1);
      started = true;
      awaitingResume = false;
      last = performance.now();
      acc = 0; syncPrevSnap();
      hudKey = '';
      audio.resume();
      paintControls();
      return;
    }
    if (t.closest('#menu')) {
      // Back to the title, not into another run. RETRY used to be the only
      // exit from a run, so leaving one meant starting another. An
      // abandoned run is deliberately NOT recorded -- it is not a result.
      newRun(seed + 1);
      started = false;
      awaitingResume = false;
      hudKey = '';
      audio.resume();
      paintControls();
      return;
    }
    if (t.id === 'retry') {
      // Always the SAME isle, win or lose -- NEXT (the #menu handler
      // above) is the only path back to level select, where a win's next
      // isle is a deliberate pick, not an automatic carry-forward. Reset
      // the clock or the accumulated wall time between runs is dumped
      // into the first tick of the new one.
      newRun(seed + 1, chapter);
      started = true;
      last = performance.now();
      acc = 0; syncPrevSnap();
      hudKey = '';
      paintControls();
    }
    audio.resume();
  });

  paintControls();

  // BLOCK THEATRE bridge: the break moment kicks the screen, dips time
  // and lands a chord — mass-scaled, per the animation bible.
  // Build identity, unmistakable on every device (audit M2): the stamp
  // ends "is my site cached?" forever.
  // `__COMMIT__` is injected by vite.config.ts's `define` from the actual
  // git SHA at build time — it was a hardcoded literal ('build
  // vivid-2026-08-14a') that never changed across builds, which is worse
  // than no stamp: it answers "which build is this" wrong instead of not
  // answering it.
  const stamp = document.createElement('div');
  stamp.textContent = `build ${__COMMIT__}`;
  stamp.style.cssText = 'position:fixed;left:6px;bottom:4px;font:10px ui-monospace,monospace;'
    + 'color:rgba(255,255,255,.45);z-index:40;pointer-events:none';
  document.body.appendChild(stamp);

  scene3d.onBlockBreak((mass) => {
    juice.addShake(0.5 + mass * 0.6);
    juice.addSlowMo(140 + mass * 160, 0.55);
    audio.gate(true, 2);
  });

  // Exposed for the perf harness and for debugging a desync.
  (window as unknown as Record<string, unknown>)['__bonwarsy'] = {
    state: () => state, hash: () => hashState(state),
    lastAlpha: () => lastAlpha,
    // D-sim-wallclock-ratio: live hit-stop timer, for a harness measuring
    // how much of real time the sim spends frozen (`juice.update`'s
    // scale=0 branch) without needing to guess from wall-clock drift.
    hitStopMs: () => juice.hitStopMs,
  };

  // First real drag retires the teaching prompt for good.
  const stage = scene3d.domElement as unknown as HTMLElement;
  stage.addEventListener('pointerdown', () => {
    if (!hasTouched) { hasTouched = true; hudKey = ''; }
    audio.resume();
  }, { passive: true });

  // Backgrounding a tab freezes requestAnimationFrame. Without this the
  // accumulated wall-clock would be dumped into the sim on return and the
  // player would come back to a run that lurched forward without them.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      last = performance.now();
      // Coming back from a call at t+180s used to drop you straight into a
      // 190-body wave already inside contact range, before your thumb was
      // even on the glass.
      if (started && state.phase === Phase.Running) { awaitingResume = true; hudKey = ''; }
    }
    acc = 0; syncPrevSnap();
  });

  /**
   * True only for a device the player can actually turn.
   *
   * "ROTATE YOUR DEVICE" is the right answer on a phone -- one wrist
   * movement, and portrait is where the corridor is composed. It is the
   * wrong answer on a desktop, where the instruction cannot be followed at
   * all, so every desktop visitor used to hit a dead end and leave. That
   * matters more than it sounds: link clicks from Reddit, HN, Discord and X
   * skew desktop, and those are the channels the corridor is the hook for.
   *
   * Desktop now gets a letterboxed portrait playfield instead of a refusal
   * (see --play-w in index.html), so there is nothing left to refuse.
   */
  const canRotate = (): boolean =>
    window.matchMedia('(hover: none) and (pointer: coarse)').matches;

  const checkOrientation = (): void => {
    const land = window.innerWidth > window.innerHeight && canRotate();
    if (land !== isLandscape) { isLandscape = land; hudKey = ''; }
  };
  checkOrientation();
  window.addEventListener('resize', checkOrientation);
  window.addEventListener('orientationchange', checkOrientation);

  last = performance.now();
  requestAnimationFrame(frame);
}

// Offline support. Registered after boot so it never delays first paint.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    // updateViaCache 'none' so the worker script itself is never served
    // from the HTTP cache — the worker is how an update arrives, so it
    // must not be the thing that goes stale.
    void navigator.serviceWorker.register('./sw.js', { updateViaCache: 'none' })
      .then((reg) => {
        // The .catch here is not decoration. Offline, `update()` cannot
        // fetch sw.js and rejects, and a bare `void reg.update()` leaves
        // that rejection unhandled — so every offline launch threw an
        // uncaught error into the console. Failing to find a new worker
        // while offline is the expected case, not an error: the cached
        // worker is exactly the one we want to keep running.
        void reg.update().catch(() => { /* offline — keep the cached worker */ });
      })
      .catch(() => { /* http:// or private mode — the game runs fine without it */ });

    // When a new worker takes over, reload ONCE so the player is actually
    // running the build that was just deployed. Without this they keep the
    // old bundle until they happen to cold-start the app again.
    let reloading = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });
  });
}

void main();
